import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog, { ConfirmOptions } from '../components/ConfirmDialog';

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

// Outside a provider nothing is confirmed: an action that needed a yes never runs on a missing question.
const ConfirmContext = createContext<Confirm>(() => Promise.resolve(false));

/** `if (!(await confirm({ title: 'Delete Sam?', confirmLabel: 'Delete user' }))) return;` Resolves false on Cancel, Escape or a click outside. */
export const useConfirm = (): Confirm => useContext(ConfirmContext);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [asking, setAsking] = useState<ConfirmOptions | null>(null);
  const settle = useRef<((answer: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((options) => {
    // A second question while one is open cancels the first.
    settle.current?.(false);
    return new Promise<boolean>((resolve) => {
      settle.current = resolve;
      setAsking(options);
    });
  }, []);

  const answer = useCallback((yes: boolean) => {
    settle.current?.(yes);
    settle.current = null;
    setAsking(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {asking && <ConfirmDialog {...asking} onConfirm={() => answer(true)} onCancel={() => answer(false)} />}
    </ConfirmContext.Provider>
  );
};
